import { useCallback, useEffect, useState } from 'react';
import { supabasePublic } from '../components/supabaseClient';

// Module-level invalidation signal. Every mutating method bumps `version`,
// and every live instance of useUserLists subscribes via useEffect so it
// re-fetches automatically. Without this, the picker mounted in a share
// modal can't see lists created from the manager modal opened above it
// — they're separate hook instances with separate state.
let listsVersion = 0;
const subscribers = new Set();
function bumpVersion() {
  listsVersion += 1;
  for (const fn of subscribers) {
    try {
      fn(listsVersion);
    } catch {
      // ignore
    }
  }
}

/**
 * Hook that owns the user-list state for the current authenticated user.
 *
 * Returns:
 *   lists          Array<{ id, name, member_count, updated_at }>
 *   loading        boolean
 *   refresh()      reload lists + member counts
 *   createList(name)
 *   renameList(id, name)
 *   deleteList(id)
 *   getMembers(id)         => Promise<string[]>
 *   addMembers(id, userIds)
 *   removeMembers(id, userIds)
 */
export function useUserLists() {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const supabase = supabasePublic();
    const { data: rows, error } = await supabase
      .from('user_lists')
      .select('id, name, updated_at')
      .order('updated_at', { ascending: false });
    if (error) {
      setLists([]);
      setLoading(false);
      return;
    }
    const ids = (rows || []).map((r) => r.id);
    let counts = new Map();
    if (ids.length > 0) {
      const { data: memberRows } = await supabase
        .from('user_list_members')
        .select('list_id, user_id')
        .in('list_id', ids);
      for (const m of memberRows || []) {
        counts.set(m.list_id, (counts.get(m.list_id) || 0) + 1);
      }
    }
    setLists(
      (rows || []).map((r) => ({
        id: r.id,
        name: r.name,
        updated_at: r.updated_at,
        member_count: counts.get(r.id) || 0,
      })),
    );
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    subscribers.add(onChange);
    return () => {
      subscribers.delete(onChange);
    };
  }, [refresh]);

  const createList = useCallback(async (name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) throw new Error('Name is required');
    const supabase = supabasePublic();
    const { data, error } = await supabase
      .from('user_lists')
      .insert({ name: trimmed })
      .select('id, name, updated_at')
      .single();
    if (error) throw error;
    await refresh();
    bumpVersion();
    return data;
  }, [refresh]);

  const renameList = useCallback(async (id, name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) throw new Error('Name is required');
    const supabase = supabasePublic();
    const { error } = await supabase
      .from('user_lists')
      .update({ name: trimmed })
      .eq('id', id);
    if (error) throw error;
    await refresh();
    bumpVersion();
  }, [refresh]);

  const deleteList = useCallback(async (id) => {
    const supabase = supabasePublic();
    const { error } = await supabase.from('user_lists').delete().eq('id', id);
    if (error) throw error;
    await refresh();
    bumpVersion();
  }, [refresh]);

  const getMembers = useCallback(async (id) => {
    const supabase = supabasePublic();
    const { data, error } = await supabase
      .from('user_list_members')
      .select('user_id')
      .eq('list_id', id);
    if (error) throw error;
    return (data || []).map((r) => r.user_id);
  }, []);

  const addMembers = useCallback(async (id, userIds) => {
    const rows = (userIds || []).map((uid) => ({ list_id: id, user_id: uid }));
    if (rows.length === 0) return;
    const supabase = supabasePublic();
    const { error } = await supabase
      .from('user_list_members')
      .upsert(rows, { onConflict: 'list_id,user_id', ignoreDuplicates: true });
    if (error) throw error;
    await refresh();
    bumpVersion();
  }, [refresh]);

  const removeMembers = useCallback(async (id, userIds) => {
    if (!userIds || userIds.length === 0) return;
    const supabase = supabasePublic();
    const { error } = await supabase
      .from('user_list_members')
      .delete()
      .eq('list_id', id)
      .in('user_id', userIds);
    if (error) throw error;
    await refresh();
    bumpVersion();
  }, [refresh]);

  return {
    lists,
    loading,
    refresh,
    createList,
    renameList,
    deleteList,
    getMembers,
    addMembers,
    removeMembers,
  };
}
